/**
 * Core logout service
 *
 * Centralizes all logout operations: Amplify signOut, storage cleanup,
 * user context clearing (Sentry, Intercom, app state), SSE disconnect,
 * and pending request cancellation.
 *
 * Used by:
 * - useAuth.logout() — main user-initiated logout flow
 * - useSSE.forceSignOut() — emergency logout during auth failures
 * - api.ts 401 handler — logout on token refresh failure
 */

import { signOut } from 'aws-amplify/auth';
import router from '@/router';
import { cancelPendingRequests } from '@/services/api';
import { sseRegistry } from '@/services/auth/sseRegistry';
import { authStorage } from '@/config/amplifyConfig';
import { clearIntercomUser } from '@/utils/intercomUtils';
import { clearSentryUser, logAppError } from '@/config/sentryConfig';
import { resetAllStores } from '@/utils/resetStores';
import useScopesStore from '@/stores/scopes';

import type { App } from 'vue';

/**
 * Enum defining reasons for logout operations
 */
export enum LogoutReason {
  SessionExpired = 'session_expired',
  InitializationError = 'initialization_error'
}

/**
 * Type for LogoutReason enum values
 */
export type LogoutReasonValue = LogoutReason;

/**
 * Helper to safely execute a cleanup step and collect errors.
 * Allows all steps to run even if individual ones fail.
 */
const executeStep = async (
  step: string,
  fn: () => Promise<void> | void,
  errors: Array<{ step: string; error: unknown }>
) => {
  try {
    await Promise.resolve(fn());
  } catch (error) {
    errors.push({ step, error });
  }
};

/**
 * Core logout operations: sign out, clear storage/context, disconnect SSE
 *
 * Best-effort cleanup that always completes. Individual step failures are
 * logged at warning level but do not prevent the logout flow from completing.
 * This ensures the app never gets stuck in a partially signed-out state.
 *
 * @param app - Optional Vue app instance for clearing global properties
 * @param reason - Optional reason for the logout (for logging/tracking)
 */
export const performLogout = async (app?: App, reason?: LogoutReasonValue) => {
  const errors: Array<{ step: string; error: unknown }> = [];

  // --- Signal logout to API layer ---
  // Prevents new requests and aborts in-flight API calls
  await executeStep(
    'cancelPendingRequests',
    () => cancelPendingRequests(),
    errors
  );

  // --- Disconnect all streams ---
  // Stop all SSE connections to prevent reconnection attempts
  await executeStep(
    'sseRegistry.disconnectAll',
    () => sseRegistry.disconnectAll(),
    errors
  );

  // --- Sign out from Amplify ---
  // Invalidate the authentication session
  await executeStep('signOut', () => signOut(), errors);

  // --- Clear browser storage ---
  // Remove auth tokens and session data
  await executeStep(
    'authStorage.clear',
    async () => {
      await authStorage.clear();
      sessionStorage.clear();
    },
    errors
  );

  // --- Clear user context (observability) ---
  // Stop tracking the user in monitoring/analytics
  await executeStep('clearSentryUser', () => clearSentryUser(), errors);
  await executeStep('clearIntercomUser', () => clearIntercomUser(), errors);

  // --- Reset application state ---
  // Clear all stores, user profile, settings, and global properties
  await executeStep(
    'resetAllStores',
    () => {
      resetAllStores();
      useScopesStore().resetTimezone();
    },
    errors
  );

  await executeStep(
    'clearGlobalProperties',
    () => {
      if (app?.config?.globalProperties) {
        (app.config.globalProperties as any).$userProfile = null;
        (app.config.globalProperties as any).$userSettings = null;
      }
    },
    errors
  );

  // --- Report any cleanup failures ---
  // Log but don't throw: logout must always complete to avoid leaving the app
  // in a partially signed-out state. Callers expect navigation to /login to succeed.
  if (errors.length > 0) {
    errors.forEach(({ step, error }) => {
      logAppError({
        error,
        functionName: 'performLogout',
        metadata: { step },
        level: 'warning'
      });
    });
  }
};

/**
 * Complete logout flow: cleanup + navigation to login
 *
 * Combines core logout operations with navigation. Always completes the
 * sign-out flow regardless of individual cleanup failures—reported errors
 * don't prevent navigation to /login.
 *
 * @param app - Optional Vue app instance for clearing global properties
 * @param reason - Optional reason for the logout (for logging/tracking)
 */
export const logout = async (app?: App, reason?: LogoutReasonValue) => {
  await performLogout(app, reason);

  try {
    const navigationTarget = {
      name: 'login',
      ...(reason ? { query: { reason } } : {})
    };
    await router.push(navigationTarget);
  } catch (error) {
    // Fallback if router navigation fails (e.g., redundant navigation)
    // Use hash-based fallback to ensure reliable login redirect
    logAppError({
      error,
      functionName: 'logout',
      section: 'router_navigation',
      level: 'warning',
      message: 'Router navigation failed, using hash-based fallback'
    });
    const queryString = reason ? `?reason=${reason}` : '';
    window.location.hash = `#/login${queryString}`;
  }
};
